<template>
  <template v-if="meterpoints.isFetching">
    <SoSkeleton class="w-50" :type="['text']" />
    <SoSkeleton class="w-50" :type="['text']" />
  </template>
  <template v-else>
    <p class="mb-4">
      Select which energy supply you'd like to manage with Smart Pay As You Go:
    </p>
    <SoCheckbox
      v-for="meterpoint in spaygMeterpointsData"
      :key="meterpoint.id"
      :model-value="activeSpaygIdentifiers.concat(upcomingSpaygIdentifiers)"
      :value="meterpoint.identifier"
      :label="getMeterpointLabel(meterpoint)"
      disabled
      multiple
      :data-testid="`meterpoint-fuel-${meterpoint.id}`"
    />
    <SoCheckbox
      v-for="meterpoint in meterpointsData"
      :key="meterpoint.id"
      v-model="model"
      :value="meterpoint.identifier"
      :label="getMeterpointLabel(meterpoint)"
      multiple
      :data-testid="`meterpoint-fuel-${meterpoint.id}`"
      :rules="[() => model.length > 0 || 'Please select at least one fuel']"
    />
  </template>
</template>

<script lang="ts">
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { ValidationRule } from "@/types/ValidationRule"
  import { defineComponent, PropType } from "vue"
  import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
  import SoSkeleton from "@/components/SoSkeleton.vue"

  export default defineComponent({
    components: {
      SoCheckbox,
      SoSkeleton,
    },

    props: {
      modelValue: {
        type: Array as PropType<string[]>,
        required: true,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: () => [],
      },
      eligibleMeterpointIdentifiers: {
        type: Array as PropType<string[]>,
        required: true,
      },
      activeSpaygIdentifiers: {
        type: Array as PropType<string[]>,
        required: false,
        default: () => [],
      },
      upcomingSpaygIdentifiers: {
        type: Array as PropType<string[]>,
        required: false,
        default: () => [],
      },
    },

    emits: ["update:modelValue"],

    data() {
      return {
        currentAccount: useCurrentAccountStore(),
      }
    },

    computed: {
      model: {
        get(): string[] {
          return this.modelValue
        },
        set(val: string[]) {
          this.$emit("update:modelValue", val)
        },
      },
      meterpoints() {
        return this.currentAccount.meterpoints
      },
      spaygMeterpointsData() {
        return this.currentAccount.meterpoints.data?.filter(
          (meterpoint) =>
            this.activeSpaygIdentifiers.includes(meterpoint.identifier) ||
            this.upcomingSpaygIdentifiers.includes(meterpoint.identifier),
        )
      },
      meterpointsData() {
        return this.currentAccount.meterpoints.data?.filter((meterpoint) => {
          const isEligible = this.eligibleMeterpointIdentifiers.includes(
            meterpoint.identifier,
          )
          const isActive = this.activeSpaygIdentifiers.includes(
            meterpoint.identifier,
          )
          const isUpcoming = this.upcomingSpaygIdentifiers.includes(
            meterpoint.identifier,
          )

          return isEligible && !isActive && !isUpcoming
        })
      },
    },

    mounted() {
      this.model = this.model.concat(this.eligibleMeterpointIdentifiers)
    },

    methods: {
      getMeterpointLabel(meterpoint: IMeterpoint) {
        return `${meterpoint.isElectricity ? "Electricity" : "Gas"} (${
          meterpoint.meterpointType
        } ${meterpoint.identifier})`
      },
    },
  })
</script>
